import { AnyAction, Reducer } from "redux";
import { castDraft, produce } from "immer";

import { createInstanceMapReducer, EMPTY_OBJECT } from "@multiweb/reactUtils";
import { DATA_SOURCE_INIT_STATE } from "../initState";
import {
    DataSourceMapState,
    IDataSourceMappedAction,
    IDataSourceState,
    IDeleteDataSourceAction,
    IInitDataSourceAction,
} from "../types";
import { actionsReducer } from "./actionsReducer";
import { attributeInfoReducer } from "./attributeInfoReducer";
import { conditionsReducer } from "./conditionsReducer";
import { configReducer } from "./configReducer";
import { dataReducer } from "./dataReducer";
import { loadingReducer } from "./loadingReducer";
import { multiselectReducer } from "./multiselectReducer";
import { orderByReducer } from "./orderByReducer";
import { workflowReducer } from "./workflowReducer";

/**
 * DataSource reducer pro jednotlivé instance.
 *
 * @param state Stav DataSource instance.
 * @param action Akce, na kterou bude reducer reagovat.
 */
const dataSourceReducer: Reducer<IDataSourceState> = (state = DATA_SOURCE_INIT_STATE, action) => {
    return produce(state, (draft) => {
        const configState = configReducer(draft.config, action);
        const attributeInfoState = attributeInfoReducer(draft.attributeInfo, action);
        const orderByState = orderByReducer(draft.orderBy, action);
        const conditionsState = conditionsReducer(draft.conditions, action);
        const loadingState = loadingReducer(draft.loading, action, draft.data.recordsIds);
        const actionsState = actionsReducer(draft.actions, action);
        const workflowActionsState = workflowReducer(draft.workflowActions, action);

        draft.config = castDraft(configState);
        draft.attributeInfo = attributeInfoState;
        draft.orderBy = castDraft(orderByState);
        draft.conditions = castDraft(conditionsState);
        draft.loading = loadingState;
        draft.actions = castDraft(actionsState);
        draft.workflowActions = castDraft(workflowActionsState);

        const dataState = dataReducer(draft.data, action, draft.config.recordIdKey);
        const multiselectState = multiselectReducer(draft.multiselect, action, {
            config: draft.config,
            recordsIds: dataState.recordsIds,
        });

        draft.data = castDraft(dataState);
        draft.multiselect = multiselectState;
    });
};

/**
 * Resolver instanceKey z akce.
 *
 * @param action Akce, ve které resolver najde instanceKey.
 */
const instanceKeyResolver = (action: AnyAction) => {
    return action instanceof IDataSourceMappedAction ? action.meta.dataSourceKey : undefined;
};

const instanceMapReducer = createInstanceMapReducer(dataSourceReducer, instanceKeyResolver);

export const reducer: Reducer<DataSourceMapState> = (state = EMPTY_OBJECT, action) => {
    if (action instanceof IDeleteDataSourceAction) {
        return produce(state, (draft) => {
            delete draft[action.meta.dataSourceKey];
        });
    } else {
        const dataSourceKey = instanceKeyResolver(action);
        if (dataSourceKey && (state[dataSourceKey] || action instanceof IInitDataSourceAction)) {
            // Do store ukládám pouze pokud je již instance vytvořená nebo pokud je akce inicializační
            return instanceMapReducer(state, action);
        }
        return state;
    }
};
